import { ChatMessage, StreamEvent, ToolInvocationEvent } from '../types';
import { requiresUiContextApproval } from '../uiContext';
import { getApprovalToolRenderer } from './ApprovalToolRenderer';
import { hasFEToolRenderer } from './FEToolRenderer';

export type ToolInvocation = ToolInvocationEvent['toolInvocation'];

export type GroupedPart =
  | StreamEvent
  | {
      type: 'tool-group';
      invocations: ToolInvocation[];
    };

const isPendingPageApproval = (invocation: ToolInvocation): boolean =>
  invocation.state === 'call' &&
  !!invocation.toolName &&
  requiresUiContextApproval(invocation.toolName);

export const isVisibleTool = (
  invocation: ToolInvocation,
  showToolErrorDetails = false,
): boolean =>
  invocation.type === 'approval' ||
  isPendingPageApproval(invocation) ||
  !!getApprovalToolRenderer(invocation.toolName) ||
  hasFEToolRenderer(invocation.toolName) ||
  (showToolErrorDetails && !!invocation.result?.isError);

export const latestInvocationsById = (
  invocations: ToolInvocation[],
): ToolInvocation[] =>
  Object.values(
    invocations.reduce<Record<string, ToolInvocation>>(
      (acc, invocation) => ({
        ...acc,
        [invocation.toolCallId]: invocation,
      }),
      {},
    ),
  );

export const groupToolParts = (
  parts: StreamEvent[],
  showToolErrorDetails = false,
): GroupedPart[] => {
  const result: GroupedPart[] = [];
  let buffer: ToolInvocation[] = [];
  const excludedToolCallIds = new Set<string>();

  const flushBuffer = () => {
    const uniqueIds = new Set(buffer.map((inv) => inv.toolCallId));
    if (uniqueIds.size >= 2) {
      result.push({ type: 'tool-group', invocations: [...buffer] });
    } else if (buffer.length >= 1) {
      const part: ToolInvocationEvent = {
        type: 'tool-invocation',
        toolInvocation: buffer[buffer.length - 1],
      };
      result.push(part);
    }
    buffer = [];
  };

  for (const part of parts) {
    if (part.type === 'tool-invocation') {
      const { toolInvocation } = part;
      const isExcluded =
        excludedToolCallIds.has(toolInvocation.toolCallId) ||
        isVisibleTool(toolInvocation, showToolErrorDetails);

      if (isExcluded) {
        excludedToolCallIds.add(toolInvocation.toolCallId);
        buffer = buffer.filter(
          (inv) => inv.toolCallId !== toolInvocation.toolCallId,
        );
        flushBuffer();
        result.push(part);
      } else {
        buffer.push(toolInvocation);
      }
    } else if (part.type !== 'step-start') {
      flushBuffer();
      result.push(part);
    }
  }
  flushBuffer();

  return result;
};

export type TurnSegment = {
  chain: GroupedPart[];
  final: GroupedPart[];
};

const isTextPart = (part: GroupedPart): boolean =>
  part.type === 'text' || part.type === 'conversation-end';

const isVisiblePart = (
  part: GroupedPart,
  showToolErrorDetails: boolean,
): boolean =>
  part.type === 'tool-invocation' &&
  isVisibleTool(part.toolInvocation, showToolErrorDetails);

const isWorkPart = (
  part: GroupedPart,
  showToolErrorDetails: boolean,
): boolean =>
  part.type === 'tool-group' ||
  (part.type === 'tool-invocation' &&
    !isVisibleTool(part.toolInvocation, showToolErrorDetails));

const nextNonTextPart = (
  parts: GroupedPart[],
  from: number,
): GroupedPart | undefined => {
  for (let i = from + 1; i < parts.length; i += 1) {
    if (!isTextPart(parts[i])) return parts[i];
  }
  return undefined;
};

const hasLaterTextPart = (parts: GroupedPart[], from: number): boolean => {
  for (let i = from + 1; i < parts.length; i += 1) {
    if (parts[i].type === 'text') return true;
  }
  return false;
};

export const splitTurnSegments = (
  parts: GroupedPart[],
  showToolErrorDetails = false,
  isTurnComplete = false,
): TurnSegment[] => {
  const segments: TurnSegment[] = [];
  let chain: GroupedPart[] = [];
  let final: GroupedPart[] = [];

  const flush = () => {
    if (chain.length === 0 && final.length === 0) return;
    segments.push({ chain, final });
    chain = [];
    final = [];
  };

  parts.forEach((part, index) => {
    if (isWorkPart(part, showToolErrorDetails)) {
      if (final.length > 0) flush();
      chain.push(part);
      return;
    }
    if (isVisiblePart(part, showToolErrorDetails)) {
      final.push(part);
      return;
    }
    if (!isTextPart(part)) return;

    const next = nextNonTextPart(parts, index);
    if (final.length > 0) {
      final.push(part);
      return;
    }
    if (
      isTurnComplete &&
      part.type === 'text' &&
      !hasLaterTextPart(parts, index)
    ) {
      final.push(part);
      return;
    }
    if (next && isWorkPart(next, showToolErrorDetails)) {
      chain.push(part);
      return;
    }
    final.push(part);
  });
  flush();

  return segments;
};

export const shouldCollapseSegmentChain = (
  segment: TurnSegment,
  isTurnComplete: boolean,
): boolean => {
  if (segment.chain.length === 0) return false;
  return isTurnComplete || segment.final.length > 0;
};

export const shouldWrapChainInThoughtProcess = (
  chain: GroupedPart[],
): boolean => chain.some(isTextPart);

export const shouldShowAssistantWorking = (
  messages: ChatMessage[],
  isLoading: boolean,
): boolean => {
  const hasRunningTools = messages.some((message) =>
    message.parts.some(
      (part) =>
        part.type === 'tool-invocation' &&
        part.toolInvocation.state === 'call' &&
        !isVisibleTool(part.toolInvocation),
    ),
  );
  if (hasRunningTools) return true;
  if (!isLoading) return false;
  const lastMessage = messages[messages.length - 1];
  if (!lastMessage || lastMessage.role === 'user') return true;

  for (let i = lastMessage.parts.length - 1; i >= 0; i -= 1) {
    const part = lastMessage.parts[i];
    if (part.type === 'step-start') continue;
    return part.type !== 'text';
  }
  return true;
};

export const countToolInvocations = (parts: GroupedPart[]): number => {
  const ids = new Set<string>();
  for (const part of parts) {
    if (part.type === 'tool-group') {
      part.invocations.forEach((inv) => ids.add(inv.toolCallId));
    } else if (part.type === 'tool-invocation') {
      ids.add(part.toolInvocation.toolCallId);
    }
  }
  return ids.size;
};
